import React, { useState, useMemo } from 'react';
import SlCalendarPicker from 'src/components/BeaconV2Components/common/SlCalendarPicker/SlCalendarPicker';
import { useAppSelector } from 'src/utils/Hooks';
import moment from 'moment-timezone';

import {
  capDateToCurrentDate,
  getFirstWeekFirstYear,
  getLastWeek,
  getWeekFirstDate,
  getWeekLastDate
} from 'src/utils/dateformatting';
import { getSearchAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

/**
 * Wrapper containing logic for SlCalendarPicker for custom application time range filters
 */
const AppCalendarContainer = ({
  handleCustomDateRangeChange,
  open,
  setCalendarOpen,
  anchorEl,
  placement
}: {
  handleCustomDateRangeChange: ({ startDate, endDate }: { startDate: Date; endDate: Date }) => void;
  open: boolean;
  setCalendarOpen: (b: boolean) => void;
  anchorEl?: HTMLElement | null;
  placement?: PopperPlacementType;
}) => {
  const retailer = useAppSelector((state) => state.retailer);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const { startWeekStartDate, endWeekEndDate, id: timePeriodType } = mainTimePeriod;

  const preFilledDates = useMemo(() => {
    return timePeriodType === 'cd' ? { start: startWeekStartDate, end: endWeekEndDate } : { start: null, end: null };
  }, []);

  /**
   * Selected date range state
   */
  const [selectedDateRange, setSelectedDateRange] = useState([preFilledDates.start, preFilledDates.end]);

  /**
   * Minimum available calendar date
   */
  const minDate = getWeekFirstDate(getFirstWeekFirstYear(allWeekIdsByRetailerId[+retailer.id]));
  /**
   * Maximum available calendar date
   */

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const maxDate =
    getSearchAppName() === AppName.Advertising
      ? capDateToCurrentDate(yesterday)
      : capDateToCurrentDate(getWeekLastDate(getLastWeek(allWeekIdsByRetailerId[+retailer.id])));

  const shouldDisableDate = (calendar: CalendarDate) => {
    const { date: calendarDate } = calendar;
    if (moment(calendarDate).isAfter(moment(maxDate)) || moment(calendarDate).isBefore(moment(minDate))) {
      return true;
    }
    return false;
  };

  const convertDateToUTC = (date): Date => {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12));
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [selectedStartDate, selectedEndDate] = dates;
    const newStartDate = convertDateToUTC(selectedStartDate);
    const newEndDate = convertDateToUTC(selectedEndDate);

    if (selectedDateRange[0] && selectedDateRange[1] && !moment(newStartDate).isSame(moment(selectedDateRange[0]), 'day')) {
      setSelectedDateRange([newStartDate, null]);
    } else {
      setSelectedDateRange([newStartDate, newEndDate]);
    }
  };

  const handleCalendarClose = () => {
    const [selectedStartDate, selectedEndDate] = selectedDateRange;
    setCalendarOpen(false);

    if (selectedStartDate !== startWeekStartDate && selectedEndDate !== endWeekEndDate) {
      handleCustomDateRangeChange({ startDate: selectedStartDate, endDate: selectedEndDate });
    }
  };

  return (
    <SlCalendarPicker
      open={open}
      onClose={handleCalendarClose}
      selectedDateRange={selectedDateRange}
      handleDateChange={handleDateChange}
      shouldDisableDate={shouldDisableDate}
      anchorEl={anchorEl}
      placement={placement}
    />
  );
};

export default AppCalendarContainer;
